// shared/chat/types.ts

import type { ArtifactKind, MediaKind } from "./events";

export interface AssistantTextPart {
  type: "text";
  text: string;
}

export interface AssistantArtifactPart {
  type: "artifact";
  artifact_id: string;
  artifact_kind: ArtifactKind;
  title: string | null;
  mime: string | null;
  language: string | null;
  text: string;
  status: "streaming" | "complete" | "failed";
  error?: string;
  /** True only when the backend marks this artifact as review-gated
   *  (status `pending_review` server-side). The streaming frames do NOT
   *  emit this today, so the approval bar stays hidden until a backend
   *  change surfaces it. Optional + defaults undefined. */
  review_required?: boolean;
  /** How the chat card renders, set by the backend tool that created the
   *  artifact. "export_only": a header with title, kind and Export, with no
   *  expand toggle or inline body (the invoice-review downloads,
   *  ELASV1-3518). Absent: the normal collapsible card. */
  display?: ArtifactDisplay;
  /** An export-only artifact's body length (the backend's `size`). Its body
   *  is never streamed or hydrated (ELASV1-3514), so this is all the card
   *  knows about it; shown next to the title. */
  size?: number;
  /** The formats the card's Export offers, set by the backend tool (code,
   *  never the model); the export route refuses any other. Absent: the menu
   *  the artifact's kind implies. */
  export_formats?: string[];
  /** A bounded, read-only glance at an export-only table (header + first
   *  rows, cells truncated server-side), since its body is never sent. */
  preview?: ArtifactTablePreview;
}

export type ArtifactDisplay = "export_only";

export interface ArtifactTablePreview {
  header: unknown[];
  rows: unknown[][];
  /** Rows in the whole table, for "Showing 5 of 68". */
  total_rows: number;
  /** What the rows are, for the caption ("🟥 rows"); "rows" when absent. */
  row_label?: string;
}

export interface AssistantMediaPart {
  type: "media";
  artifact_id: string;
  media_kind: MediaKind;
  mime: string;
  url: string;
  alt: string;
  width: number | null;
  height: number | null;
  duration_ms: number | null;
  prompt: string | null;
  status: "ready" | "expired";
}

export interface AssistantSubagentPart {
  type: "subagent";
  job_id: string;
  name: string;
  status: "running" | "succeeded" | "failed" | "cancelled";
  tools: string[];
  current_event_kind: string | null;
  events: Array<{
    seq: number;
    kind: string;
    data: any;
    ts: string;
  }>;
  result: any | null;
  error: string | null;
}

export type AssistantPart =
  | AssistantTextPart
  | AssistantArtifactPart
  | AssistantMediaPart
  | AssistantSubagentPart;

export interface Session {
  id: string;
  title: string;
  created_at: string;   // ISO
  updated_at: string;
  project_id?: string | null;
}

export interface Message {
  id: string;
  session_id: string;
  role: "user" | "assistant" | "tool" | "system";
  /** Strings for user/system/tool; parts array for assistant.
   *  String-form assistant content (legacy) is wrapped by the renderer
   *  as a single text part. */
  content: string | AssistantPart[];
  created_at: string;
  citations?: Citation[];
  /** Extended reasoning text accumulated during the turn (bot_thinking_chunk).
   *  Promoted onto the completed message so ThinkingBlock stays visible after
   *  the turn, and rehydrated from message metadata on reload. */
  thinking?: string;
  /** Measured reasoning duration (ms) for this turn so the completed
   *  ThinkingBlock reads "Thought for Xs". Promoted from the live turn and
   *  rehydrated from message metadata on reload (same as `thinking`). */
  thinkingMs?: number;
  /** Tool calls that ran during the turn, in order. Persisted so completed
   *  tool chips remain visible after the turn completes. */
  toolCalls?: { name: string; label?: string; isError?: boolean }[];
  /** Narration lines (bot_narration_promote) from this turn. Shown in the
   *  WorkingPill expanded view to give context for the tool-using steps. */
  workingNotes?: string[];
  /** Chronological work timeline (reasoning + narration + tool steps, in order)
   *  for this turn. Rehydrated from metadata.workLog; drives WorkTimeline. */
  workLog?: WorkEntry[];
  /** Total turn latency in ms (from turn_metrics). Drives "Worked for Ns". */
  durationMs?: number;
  /** Files the user attached to this message. Rendered as chips on the user
   *  bubble. Set optimistically on send and rehydrated from metadata.attachments
   *  on reload. */
  attachments?: { id: string; name: string; mime?: string }[];
  /** A user message sent mid-turn (/steer) that the turn has not read yet.
   *  The bubble says so until the backend's steer_applied echo arrives, so it
   *  never reads as a sent message waiting forever (ELASV1-3514). Client-only. */
  queued?: boolean;
  /** Persisted turn metrics for an assistant message (metadata.metrics), so the
   *  metrics footer shows after reload — not just on the live turn. Structurally
   *  matches TurnMetrics (useStreamingTurn) so it feeds TurnMetricsFooter; kept
   *  inline here to avoid a types↔hook circular import. */
  metrics?: {
    ttft_ms?: number;
    total_latency_ms?: number;
    cost_usd?: number;
    input_tokens?: number;
    output_tokens?: number;
    cache_read_input_tokens?: number;
    model?: string;
  };
  /** User feedback vote on an assistant message (metadata.feedback.vote),
   *  rehydrated on reload so the thumbs toggle reflects the persisted choice. */
  feedback?: FeedbackVote | null;
  /** The user's answer to the "keep as knowledge" card on a web-backed answer
   *  (metadata.knowledge_prompt), rehydrated on reload so the card stays gone. */
  knowledgePrompt?: KnowledgePromptState;
}

/** Thumbs feedback vote on an assistant message. */
export type FeedbackVote = "up" | "down";

/** How the user answered the "keep as knowledge" card (spec §3.3). */
export type KnowledgePromptState = "kept" | "not_useful" | "dismissed";

/** A single source citation. Shared by the legacy bot_citations frame, the new
 *  web_search_result frame, and search_knowledge tool results. `url` is optional
 *  because KB citations may lack a public URL; `type`/`ref` are optional so
 *  existing `{url, title?}` data still satisfies it. */
export interface Citation {
  url?: string;
  title?: string;
  snippet?: string;
  type?: "web" | "kb";
  ref?: string;
  /** Set when the cited source is a ready image document: a pointer only —
   *  the picture is fetched through the access-checked image endpoint. A
   *  picture-row search hit adds its row kind and, for a tile or region, the
   *  crop box in original-image pixels. */
  media?: {
    document_id: string | number;
    row_kind?: "asset" | "tile" | "region";
    crop?: [number, number, number, number];
    /** A picture inside a PDF/DOCX/PPTX: its 1-based index in the document
     *  and where it is in the original (PDF page + box in points, or the
     *  package's image part), which the image endpoint renders from. */
    picture?: number;
    locator?:
      | { page: number; box: [number, number, number, number] }
      | { part: string };
  };
}

/** One entry in a turn's chronological work timeline (WorkTimeline). Assembled
 *  in arrival order from thinking/narration/tool events. Persisted as
 *  metadata.workLog and rehydrated on reload. */
export type WorkEntry =
  | { kind: "reasoning"; text: string }
  | { kind: "narration"; text: string }
  | {
      kind: "tool";
      name: string;
      label: string;
      status: "running" | "done" | "error";
      results?: Citation[];
    };

/** A pending/resolved proposal threaded into the chat timeline as a
 *  standalone pseudo-message. The agent proposes facts and skills mid-
 *  turn (skill_proposal / fact_proposal SSE frames); the chat surface
 *  renders them as inline cards between real messages.
 *
 *  Per the 2026-06-04 plan pivot, the user accepts/rejects directly on
 *  the card. The status field tracks local optimistic updates; the
 *  source-of-truth lives in egpt_proposals.status server-side. */
export interface ProposalPseudoMessage {
  id: string; // proposal_id (UUID)
  session_id: string;
  role: "proposal";
  kind: "fact" | "skill";
  /** Payload shape depends on kind. Fact: { category, text, evidence? }.
   *  Skill: { name, description, body }. We keep it loosely typed here
   *  and discriminate at render time so this file doesn't need to
   *  import the API client. */
  payload: Record<string, unknown>;
  status: "pending" | "accepted" | "rejected" | "expired";
  trigger_kind: string;
  created_at: string;
  expires_at: string;
}

/** Anything that can sit in the chat timeline. Components iterate this
 *  union and dispatch on `role`. */
export type TimelineEntry = Message | ProposalPseudoMessage;

export interface ChatPanelFeatures {
  attachments: boolean;
  settings: boolean;
  thinking: boolean;
  editRegenerate: boolean;
  /** Show the project badge in the chat header (T14). */
  projects: boolean;
  /** Show the composer "Plan" toggle (blocking plan mode). The toggle sends
   *  payload.mode=plan; the backend only enforces gating when its own
   *  UNIFIED_AGENT_PLAN_MODE_ENABLED flag is on, so this is safe-on. */
  planMode: boolean;
  /** Show the composer "Run in background" toggle. Sends
   *  payload.background_subagents; gated on the backend background_subagents
   *  capability (subagents enabled). */
  backgroundSubagents: boolean;
  /** Collapse plan-mode and run-in-background into a single "+" menu button
   *  instead of individual icon buttons. Useful for embed contexts where
   *  toolbar space is limited. Default false = individual buttons. */
  actionsMenu: boolean;
  /** Start the WorkTimeline disclosure collapsed even while the model is
   *  actively thinking/working. Users can still expand it. Default false =
   *  open while streaming, auto-collapse when the answer starts. */
  thinkingCollapsed: boolean;
  /** Offer to keep a web-backed answer as a personal knowledge document (the
   *  card above the composer, spec §3.3). Off in embeds, which have no
   *  Documents page to land on. */
  keepWebAnswers: boolean;
}

export const DEFAULT_FEATURES: ChatPanelFeatures = {
  attachments: true,
  settings: true,
  thinking: true,
  editRegenerate: true,
  projects: true,
  planMode: true,
  backgroundSubagents: true,
  actionsMenu: false,
  thinkingCollapsed: false,
  keepWebAnswers: true,
};
